/* ==========================================================================
   HUSSIENI HOME — SHOP BEHAVIOUR
   Filter drawer, sort, gallery counter, header reveal, recommendations.
   No dependencies. Every handler is passive or rAF-coalesced so scrolling
   stays on the compositor.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Filter drawer
     Opens as a bottom sheet on mobile and a side panel on desktop. Focus is
     moved into the panel and trapped while open, and the background is
     locked so the grid does not scroll underneath.
     --------------------------------------------------------------------- */
  function initFilters() {
    var drawer = document.querySelector('[data-hh-drawer]');
    if (!drawer) return;

    var openers = document.querySelectorAll('[data-hh-filter-open]');
    var panel = drawer.querySelector('[data-hh-filter-form]');
    var lastFocused = null;

    function focusables() {
      return panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])'
      );
    }

    function open() {
      lastFocused = document.activeElement;
      drawer.hidden = false;
      // One frame between unhiding and adding the class, or the transition
      // has nothing to animate from.
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
        document.body.classList.add('hh-drawer-open');
        var f = focusables();
        if (f.length) f[0].focus();
      });
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    }

    function close() {
      drawer.classList.remove('is-open');
      document.body.classList.remove('hh-drawer-open');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      var delay = reduced ? 0 : 380;
      window.setTimeout(function () { drawer.hidden = true; }, delay);
      if (lastFocused) lastFocused.focus();
    }

    openers.forEach(function (b) { b.addEventListener('click', open); });
    drawer.querySelectorAll('[data-hh-filter-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (e) {
      if (drawer.hidden) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // Show how many facets are currently applied, so the button carries
    // state without opening the drawer.
    var badge = document.querySelector('[data-hh-active-count]');
    if (badge) {
      var applied = panel.querySelectorAll('input[type="checkbox"]:checked').length;
      if (applied > 0) { badge.textContent = applied; badge.hidden = false; }
    }
  }

  /* ---------------------------------------------------------------------
     Sort
     Rewrites sort_by on the current URL and reloads, preserving any active
     filter parameters rather than discarding them.
     --------------------------------------------------------------------- */
  function initSort() {
    var select = document.querySelector('[data-hh-sort]');
    if (!select) return;
    select.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  }

  /* ---------------------------------------------------------------------
     Gallery counter (mobile swipe)
     --------------------------------------------------------------------- */
  function initGallery() {
    var gallery = document.querySelector('[data-hh-gallery]');
    if (!gallery) return;
    var now = gallery.querySelector('[data-hh-counter-now]');
    if (!now) return;
    var shots = gallery.querySelectorAll('[data-hh-shot]');
    var ticking = false;

    function update() {
      var i = Math.round(gallery.scrollLeft / gallery.clientWidth) + 1;
      now.textContent = Math.min(Math.max(i, 1), shots.length);
      ticking = false;
    }

    gallery.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
  }

  /* ---------------------------------------------------------------------
     Header: hide on scroll down, reveal on scroll up
     --------------------------------------------------------------------- */
  function initHeader() {
    var last = window.scrollY;
    var ticking = false;
    var THRESHOLD = 6; // ignore sub-pixel jitter and rubber-banding

    function update() {
      var y = window.scrollY;
      var delta = y - last;

      if (Math.abs(delta) > THRESHOLD) {
        // Never hide the header while a drawer is open, or the close
        // control can scroll out of reach.
        var locked = document.body.classList.contains('hh-drawer-open');
        if (delta > 0 && y > 200 && !locked) {
          document.body.classList.add('hh-nav-hidden');
        } else {
          document.body.classList.remove('hh-nav-hidden');
        }
        last = y;
      }
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
  }

  /* ---------------------------------------------------------------------
     Recommendations
     Fetched after paint so the product page's own content is never delayed
     by a related-products query.
     --------------------------------------------------------------------- */
  function initRecs() {
    var el = document.querySelector('[data-hh-recs]');
    if (!el || !el.dataset.url) return;

    var url = el.dataset.url;
    if (el.dataset.intent) url += '&intent=' + encodeURIComponent(el.dataset.intent);

    fetch(url)
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-hh-recs]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          document.dispatchEvent(new CustomEvent('hh:content-added'));
        }
      })
      .catch(function () {
        // A failed recommendation fetch must never break the page; the
        // section simply stays empty.
      });
  }

  function boot() {
    initFilters();
    initSort();
    initGallery();
    initHeader();
    initRecs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
